import { useEffect, useState } from 'react'
import { useChannel } from '@/context/ChannelContext'
import { ShowcaseState, ConsentPayload, ChoosingPayload, UpdateShowcaseSettingsPayload } from '@/api/showcase'
import { track } from '@/lib/analytics'
import { StepIntro } from './StepIntro'
import { StepConsent } from './StepConsent'
import { StepChoosing } from './StepChoosing'
import { StepProcessing } from './StepProcessing'

type ShowcaseActions = {
  activateIntro: () => Promise<ShowcaseState | undefined>
  activateConsent: (payload: ConsentPayload) => Promise<ShowcaseState | undefined>
  activateChoosing: (payload: ChoosingPayload) => Promise<ShowcaseState | undefined>
  updateSettings: (payload: UpdateShowcaseSettingsPayload) => Promise<ShowcaseState | undefined>
  disable: () => Promise<ShowcaseState | undefined>
  enable: () => Promise<ShowcaseState | undefined>
}

/**
 * Activation flow для Витрины — рендерится когда status ∈ {intro, consent, choosing, processing}.
 *
 * Шаги:
 *   1. Знакомство (3 карточки + пруф 84К/28К)
 *   2. Согласие (3 чекбокса + URL preview)
 *   3. Выбор что публиковать (radio Все / Длинные / Вручную)
 *   4. Обработка (прогресс-бар + polling до active)
 *
 * Состояние модуля — driver: бэк сам переключает между шагами при успешных
 * activate/* запросах. Если юзер начал и закрыл — при возврате попадёт ровно
 * на тот шаг где остановился.
 */
export function ActivationFlow({
  state,
  actions,
  refetch,
}: {
  state: ShowcaseState
  actions: ShowcaseActions
  refetch: () => Promise<void>
}) {
  const { selectedChannel } = useChannel()
  // Шаг для UI выводится из бэк-статуса. step1 = intro, step2 = consent, ...
  const stepFromStatus = STATUS_TO_STEP[state.status] ?? 1
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Аналитика просмотра шага
  useEffect(() => {
    track('showcase_step_view', {
      step: state.status,
      stepNumber: stepFromStatus,
      message: `showcase · ${state.status} (${stepFromStatus}/4)`,
    })
  }, [state.status, stepFromStatus])

  // Step 1 → Step 2: согласие пока не нужно — просто визуально следующий шаг.
  // Но бэк всё ещё в intro, перевод в consent делается на сабмите чекбоксов.
  const [showStep1, setShowStep1] = useState(state.status === 'intro')
  useEffect(() => {
    // Если бэк уехал дальше intro — скрываем экран знакомства
    if (state.status !== 'intro') setShowStep1(false)
  }, [state.status])

  function handleStep1Next() {
    track('showcase_activation_started', { message: 'клик «Дальше» на знакомстве' })
    setShowStep1(false) // Локальный переход — Step 2 (бэк всё ещё intro)
  }

  async function handleConsentSubmit(checkboxes: [boolean, boolean, boolean]) {
    if (!selectedChannel) return
    setSubmitting(true)
    setError(null)
    try {
      // 1) Переводим intro → consent в бэке (фиксируем сам факт согласия)
      await actions.activateConsent({
        consentVersion: 'v1-2026-06',
        checkboxes,
        previewUrl: `https://telegraphyx.ru/channels/${encodeURIComponent(selectedChannel.maxChannelID)}`,
      })
      track('showcase_consent_given', {
        consentVersion: 'v1-2026-06',
        message: 'согласие принято',
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить согласие')
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  }

  async function handleChoosingSubmit(whatToPublish: 'all' | 'long' | 'manual', backfillRequested: boolean) {
    setSubmitting(true)
    setError(null)
    try {
      await actions.activateChoosing({ whatToPublish, backfillRequested })
      track('showcase_choosing_done', {
        whatToPublish,
        backfillRequested,
        message: `выбор «${whatToPublish}» · бэкфилл: ${backfillRequested ? 'да' : 'нет'}`,
      })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось запустить публикацию')
      setSubmitting(false)
    } finally {
      setSubmitting(false)
    }
  }

  // Соответствие бэк-статуса UI-шагу (после ревизий 16-17.06.2026):
  //  - intro + showStep1=true   → Step1 (знакомство)
  //  - intro + showStep1=false  → Step2 (форма согласия) — бэк ещё intro,
  //    activateConsent ещё не вызван
  //  - consent                  → Step3 (форма выбора)
  //  - choosing                 → Step4 (юзер сделал выбор, бэк запустил initial-publish)
  //  - processing               → Step4 (обработка)
  const currentStep = (() => {
    if (state.status === 'processing' || state.status === 'choosing') return 4
    if (state.status === 'consent') return 3
    // intro: либо Step1 (если showStep1), либо локально Step2
    return showStep1 ? 1 : 2
  })()

  return (
    <div className="act-flow">
      <Stepper current={currentStep} />

      {currentStep === 1 && <StepIntro onNext={handleStep1Next} />}

      {currentStep === 2 && (
        <StepConsent
          channel={selectedChannel ?? null}
          submitting={submitting}
          error={error}
          onSubmit={handleConsentSubmit}
          onBack={() => setShowStep1(true)}
        />
      )}

      {currentStep === 3 && (
        <StepChoosing
          submitting={submitting}
          error={error}
          onSubmit={handleChoosingSubmit}
        />
      )}

      {currentStep === 4 && (
        <StepProcessing state={state} onRefetch={refetch} />
      )}

      <style>{`
        .act-flow {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
      `}</style>
    </div>
  )
}

const STATUS_TO_STEP: Record<string, number> = {
  intro: 1,
  consent: 2,
  choosing: 3,
  processing: 4,
}

function Stepper({ current }: { current: number }) {
  const steps = [
    { n: 1, label: 'Знакомство' },
    { n: 2, label: 'Согласие' },
    { n: 3, label: 'Выбор' },
    { n: 4, label: 'Готово' },
  ]
  return (
    <div className="stepper">
      {steps.map((s, i) => {
        const isDone = current > s.n
        const isCurrent = current === s.n
        return (
          <div key={s.n} className={`step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''}`}>
            <div className="step-pill">
              {isDone ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5" />
                </svg>
              ) : (
                s.n
              )}
            </div>
            <span className="step-label">{s.label}</span>
            {i < steps.length - 1 && <div className="step-bar" />}
          </div>
        )
      })}

      <style>{`
        .stepper {
          display: flex;
          align-items: center;
          gap: 0;
          padding: 8px 0;
        }
        .step {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1;
        }
        .step:last-child { flex: 0 0 auto; }
        .step-pill {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          background: var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #5b6678);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
          flex-shrink: 0;
          transition: background 200ms, color 200ms;
        }
        .step-pill svg { width: 14px; height: 14px; }
        .step.current .step-pill {
          background: var(--tg-blue, #0066ff);
          color: white;
          box-shadow: 0 0 0 4px rgba(0,102,255,0.15);
        }
        .step.done .step-pill {
          background: var(--tg-up, #00a860);
          color: white;
        }
        .step-label {
          font-size: 13px;
          font-weight: 600;
          color: var(--tg-mute, #5b6678);
          white-space: nowrap;
        }
        .step.current .step-label { color: var(--tg-ink, #111); }
        .step.done .step-label { color: var(--tg-up, #00a860); }
        .step-bar {
          flex: 1;
          height: 2px;
          background: var(--tg-line-2, #f0f0f4);
          margin: 0 8px;
          border-radius: 1px;
          transition: background 200ms;
        }
        .step.done .step-bar { background: var(--tg-up, #00a860); }
        @media (max-width: 600px) {
          .step-label { display: none; }
          .step-bar { margin: 0 4px; }
        }
      `}</style>
    </div>
  )
}
